'use client'

import InputWrapper from '@trustoo/components/input/InputWrapper'
import clsx from 'clsx'
import {
    ComponentPropsWithoutRef,
    CSSProperties,
    FocusEvent,
    forwardRef,
    ReactNode,
    useState,
} from 'react'

export interface InputFieldProps extends Omit<ComponentPropsWithoutRef<'input'>, 'ref'> {
    error?: string | boolean
    defaultErrorMsg?: string
    legend?: string
    animatedPlaceholder?: ReactNode
    before?: ReactNode
    after?: ReactNode
    labelClassName?: string
    labelStyle?: CSSProperties
    inputClassName?: string
    errorClassName?: string
    withErrorIcon?: boolean
    dataCy?: string
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
    (
        {
            type = 'text',
            value,
            error,
            defaultErrorMsg,
            legend,
            placeholder,
            animatedPlaceholder,
            autoFocus,
            before,
            after,
            onBlur: onBlurProp,
            onFocus: onFocusProp,
            className,
            labelClassName,
            labelStyle,
            inputClassName,
            errorClassName,
            withErrorIcon = false,
            dataCy,
            ...restInputProps
        },
        ref,
    ) => {
        const [focused, setFocused] = useState(false)

        const onFocus = (e: FocusEvent<HTMLInputElement>) => {
            if (restInputProps.readOnly) {
                const activeElement = document.activeElement as HTMLElement | null
                if (activeElement && typeof activeElement.blur === 'function') {
                    activeElement.blur()
                }
            } else {
                setFocused(true)
            }
            onFocusProp?.(e)
        }

        const onBlur = (e: FocusEvent<HTMLInputElement>) => {
            setFocused(false)
            onBlurProp?.(e)
        }

        const hasValue = value !== '' && value !== undefined && value !== null

        return (
            <InputWrapper
                id={restInputProps.id}
                hasValue={hasValue}
                error={error}
                isFocused={focused}
                isDisabled={restInputProps.disabled}
                legend={legend}
                animatedPlaceholder={animatedPlaceholder}
                className={className}
                labelClassName={clsx('gap-x-3.5 h-[46px] px-3.5', labelClassName)}
                labelStyle={labelStyle}
                errorClassName={errorClassName}
                defaultErrorMsg={defaultErrorMsg}
                withErrorIcon={withErrorIcon}>
                {before}
                <input
                    {...restInputProps}
                    data-cy={dataCy}
                    ref={ref}
                    value={value}
                    type={type}
                    autoFocus={autoFocus}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    className={clsx(
                        'w-[100px] flex-1 h-full py-3',
                        'text-dark leading-4 border-none rounded box-border',
                        'transition-[border] duration-[140ms] ease-in-out',
                        'overflow-hidden text-ellipsis',
                        'outline-none',
                        'disabled:cursor-not-allowed disabled:bg-[inherit] disabled:text-text-secondary',
                        'read-only:text-text-secondary',
                        'placeholder:text-ellipsis',
                        focused ? 'placeholder:text-[#b6b9cd]' : 'placeholder:text-[#80868b]',
                        inputClassName,
                    )}
                />
                {after}
            </InputWrapper>
        )
    },
)

InputField.displayName = 'InputField'

export default InputField
